import { useEffect, useState } from "react";
import { useScroll } from "../scroll/ScrollProvider";
import { EnquiryForm } from "./EnquiryForm";

const MENU_LINKS: { href: string; label: string }[] = [
  { href: "#mandate", label: "Mandate" },
  { href: "#gold", label: "Gold" },
  { href: "#digital", label: "Digital" },
  { href: "#banking", label: "Banking" },
  { href: "#cognitive", label: "Cognitive" },
  { href: "#ai", label: "AI" },
  { href: "#datacenters", label: "Compute" },
  { href: "#power", label: "Power" },
  { href: "#carbon", label: "Carbon" },
  { href: "#bhutan", label: "Bhutan" },
  { href: "#network", label: "Network" },
  { href: "#contact", label: "Contact" },
];

export function Overlay() {
  const { section, progress } = useScroll();
  const [hideCue, setHideCue] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    setHideCue(progress > 0.04);
  }, [progress]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!scrolled) setDropdownOpen(false);
  }, [scrolled]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <>
      <header className="nav">
        <a className="mark" href="#hero" aria-label="FIVE QUANTUM home" onClick={closeMenu}>
          <img
            src="/brand/five-quantum-gold.png"
            alt=""
            width={28}
            height={26}
            style={{ verticalAlign: "middle", marginRight: 12 }}
          />
          FIVE <span>QUANTUM</span>
        </a>
        <button
          type="button"
          className={`menu-toggle ${menuOpen ? "menu-toggle--open" : ""}`}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="primary-menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </header>
      <nav
        id="primary-menu"
        className={`top-menu ${menuOpen ? "top-menu--open" : ""} ${
          scrolled ? "top-menu--collapsed" : ""
        } ${dropdownOpen ? "top-menu--dropdown-open" : ""}`}
        aria-label="Primary"
        onMouseLeave={() => setDropdownOpen(false)}
      >
        <button
          type="button"
          className="top-menu__pill"
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
          onClick={() => setDropdownOpen((v) => !v)}
          onMouseEnter={() => setDropdownOpen(true)}
        >
          <span>Menu</span>
          <span className="top-menu__caret" aria-hidden="true">▾</span>
        </button>
        <ul className="top-menu__list">
          {MENU_LINKS.map((link) => (
            <li key={link.href}>
              <a className="top-menu__link" href={link.href} onClick={closeMenu}>
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      {menuOpen && (
        <button
          type="button"
          className="menu-scrim"
          aria-label="Close menu"
          onClick={closeMenu}
        />
      )}

      <nav className="rail" aria-label="Section index">
        {Array.from({ length: 13 }).map((_, i) => (
          <span key={i} className={`tick ${section === i ? "active" : ""}`} />
        ))}
      </nav>

      <div className={`scroll-cue ${hideCue ? "hidden" : ""}`}>Scroll</div>

      <div className="scene-dim" />
      <div className="vignette" />

      <main className="scroll-spacer">
        {/* 0 — Hero */}
        <section id="hero" className="section">
          <div className="content">
            <div className="eyebrow">FIVE QUANTUM</div>
            <h1 className="headline">
              Orchestrating access to <em>exclusive</em> asset classes.
            </h1>
            <p className="subcopy">
              A private gateway between sovereign-grade commodities, blockchain-leveraged
              digital assets, and cognitive infrastructure — engineered for global foundations,
              family offices, and sovereign capital.
            </p>
          </div>
        </section>

        {/* 1 — Mandate */}
        <section id="mandate" className="section section--right">
          <div className="content">
            <div className="eyebrow">Our Mandate</div>
            <h2 className="headline">
              Frictionless execution. <em>Absolute</em> discretion.
            </h2>
            <p className="subcopy">
              FIVE QUANTUM operates at the nexus of physical permanence and digital velocity —
              retained by elite institutional and private entities to navigate the most complex,
              high-stakes asset acquisitions and transfers on earth.
            </p>
            <ul className="pillars">
              <li>
                <span className="pillar-title">Mitigation of Legal Exposure</span>
                <span className="pillar-desc">
                  Pre-emptive jurisdictional and counterparty risk control through meticulous
                  legal structuring.
                </span>
              </li>
              <li>
                <span className="pillar-title">Rigorous Privacy</span>
                <span className="pillar-desc">
                  Cryptographic infrastructure and fortified corporate veils protect client
                  identity and transactional intent.
                </span>
              </li>
              <li>
                <span className="pillar-title">Global Commercial Legitimacy</span>
                <span className="pillar-desc">
                  Fully compliant, mathematically verified commercial transactions across
                  sovereign borders.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* 2 — Sovereign-Grade Bullion */}
        <section id="gold" className="section">
          <div className="content">
            <div className="eyebrow">01 · Sovereign-Grade Precious Metals</div>
            <h2 className="headline">
              Off-market <em>certified</em> reserves. Fortified corridors.
            </h2>
            <p className="subcopy">
              Unparalleled access to globally certified precious metals — secure acquisition, custodial
              vaulting in non-extradition jurisdictions, and seamless transfer of physical
              tonnage. Direct seller mandates with LBMA-affiliated refineries; strictly off-ledger
              to the broader public market.
            </p>
          </div>
        </section>

        {/* 3 — Institutional Digital Assets */}
        <section id="digital" className="section section--right">
          <div className="content">
            <div className="eyebrow">02 · Institutional Digital Assets</div>
            <h2 className="headline">
              Bridging legacy capital with <em>decentralized</em> liquidity.
            </h2>
            <p className="subcopy">
              Secure acquisition, custody, and transfer of high-volume digital assets — Bitcoin,
              Ether, and tokenized commodity exposures. We engineer the bridge between
              traditional financial systems and cryptographic networks: frictionless liquidity,
              comprehensive sovereignty.
            </p>
          </div>
        </section>

        {/* 4 — Global Banking Navigation */}
        <section id="banking" className="section">
          <div className="content">
            <div className="eyebrow">03 · Global Banking Navigation</div>
            <h2 className="headline">
              Bespoke clearing corridors at the <em>highest</em> echelons.
            </h2>
            <p className="subcopy">
              Operating beyond retail and standard corporate banking. We structure direct
              relationships with apex institutions — private banks, multi-jurisdictional clearing
              partners, and sovereign desks — to keep capital velocity uninterrupted by
              bureaucratic stagnation.
            </p>
          </div>
        </section>

        {/* 5 — Cognitive Infrastructure */}
        <section id="cognitive" className="section section--right">
          <div className="content">
            <div className="eyebrow">04 · Cognitive Infrastructure</div>
            <h2 className="headline">
              A humanoid <em>intelligence</em> layer for capital orchestration.
            </h2>
            <p className="subcopy">
              We architect bespoke AI and cognitive systems that observe global markets in
              real time — risk surveillance, jurisdictional pattern recognition, and
              autonomous execution across the gold, digital, and banking layers. Built for
              principals who require strategic asymmetry.
            </p>
          </div>
        </section>

        {/* 6 — AI Integration & Forward Development (AI-as-a-Service) */}
        <section id="ai" className="section">
          <div className="content">
            <div className="eyebrow">05 · AI Integration &amp; Forward Development</div>
            <h2 className="headline">
              Embedding <em>artificial intelligence</em> across our clients&rsquo; operations.
            </h2>
            <p className="subcopy">
              FIVE QUANTUM delivers AI-as-a-Service for principals and their portfolio
              companies — translating frontier model capability into measurable enterprise
              outcomes. From discovery through deployment, we architect the systems that
              automate decision flow, augment specialist labor, and create durable strategic
              advantage.
            </p>
            <ul className="pillars">
              <li>
                <span className="pillar-title">Strategic AI Audit &amp; Roadmap</span>
                <span className="pillar-desc">
                  End-to-end assessment of operational surface area, with a prioritised
                  roadmap of automations and AI-augmented workflows ranked by ROI and risk.
                </span>
              </li>
              <li>
                <span className="pillar-title">Custom Agent &amp; Automation Build</span>
                <span className="pillar-desc">
                  Bespoke agentic systems, RAG knowledge bases, and workflow automations
                  embedded directly into existing tooling — CRM, ERP, communications, and
                  internal data lakes.
                </span>
              </li>
              <li>
                <span className="pillar-title">Forward Development &amp; Managed Operations</span>
                <span className="pillar-desc">
                  Continuous improvement under retainer: model evaluations, fine-tuning,
                  guardrails, observability, and forward-deployed engineering aligned to
                  evolving business priorities.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* 7 — Data Centers / Compute */}
        <section id="datacenters" className="section section--right">
          <div className="content">
            <div className="eyebrow">06 · Data Centers &amp; Compute</div>
            <h2 className="headline">
              Sovereign-grade <em>compute</em> for the cognitive era.
            </h2>
            <p className="subcopy">
              We structure equity, debt, and joint-venture access to large-scale data center
              development — hyperscaler-tier facilities, GPU clusters for institutional AI
              workloads, and compute capacity contracts. Sites secured against sovereign-grade
              power agreements and long-horizon offtake.
            </p>
            <ul className="pillars">
              <li>
                <span className="pillar-title">Greenfield &amp; Brownfield Sites</span>
                <span className="pillar-desc">
                  Pre-permitted, grid-connected campuses across Tier-1 and emerging
                  jurisdictions, with optional sovereign offtake agreements.
                </span>
              </li>
              <li>
                <span className="pillar-title">GPU &amp; Accelerator Capacity</span>
                <span className="pillar-desc">
                  Allocation contracts for H100 / B200-class clusters via institutional
                  pre-orders and existing operator relationships.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* 7 — Power Equipment for Sale */}
        <section id="power" className="section">
          <div className="content">
            <div className="eyebrow">07 · Power Equipment &amp; Energy Assets</div>
            <h2 className="headline">
              Industrial-grade power infrastructure, <em>institutionally</em> sourced.
            </h2>
            <p className="subcopy">
              FIVE QUANTUM brokers the sale and acquisition of high-voltage transformers,
              gas and steam turbines, modular substations, switchgear, and grid-tie
              equipment — sourced through OEM and sovereign-affiliated channels and
              delivered with full provenance, certification, and logistics control.
            </p>
            <ul className="pillars">
              <li>
                <span className="pillar-title">Generation</span>
                <span className="pillar-desc">
                  Gas / steam turbines, hydro and modular nuclear units, and reciprocating
                  engines for utility-scale and behind-the-meter deployment.
                </span>
              </li>
              <li>
                <span className="pillar-title">Transmission &amp; Distribution</span>
                <span className="pillar-desc">
                  HV transformers, GIS switchgear, mobile substations, and specialty cabling
                  — including discontinued and long-lead-time inventory.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* 8 — Carbon Credits & Climate Assets */}
        <section id="carbon" className="section section--right">
          <div className="content">
            <div className="eyebrow">08 · Carbon Credits &amp; Climate Assets</div>
            <h2 className="headline">
              Verified offsets at <em>institutional</em> scale.
            </h2>
            <p className="subcopy">
              Sourcing, custody, and structured retirement of high-integrity carbon
              credits — Verra (VCS), Gold Standard, and ART/TREES — alongside nature-based
              and engineered removal portfolios. We aggregate sovereign-aligned project
              pipelines and direct offtake from forest, blue-carbon, and direct-air-capture
              programmes for principals seeking compliance-grade and reputational-grade
              decarbonisation.
            </p>
          </div>
        </section>

        {/* 9 — Bhutan Foreign Direct Investment */}
        <section id="bhutan" className="section section--bhutan">
          <div className="content content--wide">
            <div className="eyebrow">Sovereign Opportunity · Royal Government of Bhutan</div>
            <h2 className="headline">
              Foreign direct investment into the <em>Kingdom of Bhutan</em>.
            </h2>
            <p className="subcopy" style={{ maxWidth: "62ch" }}>
              FIVE QUANTUM offers a discreet introduction into the Kingdom of Bhutan&rsquo;s
              official Foreign Direct Investment programme — administered under the
              Department of Industry, Ministry of Industry, Commerce &amp; Employment, in
              accordance with the FDI Policy of the Royal Government of Bhutan. A
              sovereign-grade opportunity for foundations, family offices, and treaty-grade
              capital seeking long-horizon allocation in a stable, carbon-negative
              jurisdiction.
            </p>

            <div className="bhutan-grid">
              <div className="bhutan-stat">
                <div className="bhutan-stat__value">100%</div>
                <div className="bhutan-stat__label">Repatriation of Profits &amp; Dividends</div>
              </div>
              <div className="bhutan-stat">
                <div className="bhutan-stat__value">Carbon −</div>
                <div className="bhutan-stat__label">Net-Negative Sovereign Jurisdiction</div>
              </div>
              <div className="bhutan-stat">
                <div className="bhutan-stat__value">Tier-1</div>
                <div className="bhutan-stat__label">Hydropower &amp; Renewable Compute Power</div>
              </div>
            </div>

            <div className="bhutan-sectors">
              <h3 className="bhutan-subhead">Priority Investment Sectors</h3>
              <ul className="bhutan-list">
                <li>Hydropower &amp; renewable energy generation</li>
                <li>Tier-III/IV data centers powered by surplus hydroelectricity</li>
                <li>Hospitality &amp; high-value sustainable tourism</li>
                <li>Education, healthcare &amp; specialised wellness</li>
                <li>Information technology &amp; digital infrastructure</li>
                <li>Manufacturing, agro-processing &amp; high-value mineral resources</li>
              </ul>
            </div>

            <p className="bhutan-footnote">
              Engagements are coordinated under formal mandate with Bhutanese governmental
              counterparts and authorised local advisory partners. All disclosures and
              investment thresholds are governed by the FDI Policy &amp; Rules &amp;
              Regulations of the Royal Government of Bhutan.
            </p>
            <p className="bhutan-footnote">
              The accompanying visual sequence is presented as a respectful homage to
              Bhutan&rsquo;s cultural and royal heritage, reflecting our commitment to
              dignity, trust, and long-horizon partnership in the Kingdom.
            </p>
          </div>
        </section>

        {/* 9 — Institutional Partner Network */}
        <section id="network" className="section section--network">
          <div className="content content--wide">
            <div className="eyebrow">Institutional Partner Network</div>
            <h2 className="headline">
              A global team behind the <em>three</em> principals.
            </h2>
            <p className="subcopy" style={{ maxWidth: "60ch" }}>
              FIVE QUANTUM operates through an orchestrated mesh of vetted counterparties
              spanning private banking, sovereign refining, and ultra-high-net-worth advisory.
              All relationships are governed by formal mandates and confidentiality protocols;
              named counterparties are disclosed only under executed NDAs.
            </p>

            <div className="network-grid">
              <article className="network-card">
                <div className="network-card__icon" aria-hidden="true">
                  <span className="network-card__badge" title="Banking">
                    🏦
                  </span>
                </div>
                <h3 className="network-card__title">Private &amp; Investment Banks</h3>
                <p className="network-card__body">
                  Established corridors with Tier-1 European, Swiss, Liechtenstein, and Gulf
                  private banks. Structured introductions, paymaster services, and
                  multi-jurisdictional clearing.
                </p>
                <span className="network-card__meta">EU · CH · LI · GCC · APAC</span>
              </article>

              <article className="network-card">
                <div className="network-card__icon" aria-hidden="true">
                  <span className="network-card__badge" title="Wealth Management">
                    💼
                  </span>
                </div>
                <h3 className="network-card__title">Private Wealth Managers</h3>
                <p className="network-card__body">
                  Discreet co-mandates with multi-family offices, fiduciaries, and
                  ultra-high-net-worth advisory desks managing inter-generational and
                  sovereign-aligned capital.
                </p>
                <span className="network-card__meta">Multi-Family Offices · Fiduciaries</span>
              </article>

              <article className="network-card">
                <div className="network-card__icon" aria-hidden="true">
                  <span className="network-card__badge" title="Gold Refinery">
                    🪙
                  </span>
                </div>
                <h3 className="network-card__title">Gold Refinery Seller Mandates</h3>
                <p className="network-card__body">
                  Direct seller mandates with LBMA-affiliated and sovereign-grade gold
                  refineries. Verified provenance, certified bars, and structured allocation
                  for physical-tonnage transactions.
                </p>
                <span className="network-card__meta">CH · AE · SG · ZA · TR</span>
              </article>

              <article className="network-card">
                <div className="network-card__icon" aria-hidden="true">
                  <span className="network-card__badge" title="Oil Refinery">
                    🛢️
                  </span>
                </div>
                <h3 className="network-card__title">Oil Refinery Seller Mandates</h3>
                <p className="network-card__body">
                  Mandate relationships with state-aligned and independent crude / product
                  refineries. JP54, EN590, D6, Jet A1, and LNG corridors structured under
                  formal SCO/ICPO protocols.
                </p>
                <span className="network-card__meta">RU · GCC · CIS · LATAM</span>
              </article>

              <article className="network-card">
                <div className="network-card__icon" aria-hidden="true">
                  <span className="network-card__badge" title="Sovereign and Foundation">
                    👑
                  </span>
                </div>
                <h3 className="network-card__title">Sovereign &amp; Foundation Desks</h3>
                <p className="network-card__body">
                  Coordinated access to sovereign wealth desks, royal foundations, and
                  state-affiliated investment offices for treaty-grade capital deployment.
                </p>
                <span className="network-card__meta">By formal introduction only</span>
              </article>

              <article className="network-card">
                <div className="network-card__icon" aria-hidden="true">
                  <span className="network-card__badge" title="Legal and Compliance">
                    ⚖️
                  </span>
                </div>
                <h3 className="network-card__title">Legal &amp; Compliance Counsel</h3>
                <p className="network-card__body">
                  Embedded counsel covering ICC compliance, AML/KYC, cross-border treaty
                  structuring, and instrument-grade transaction documentation.
                </p>
                <span className="network-card__meta">ICC · FATF · OECD-aligned</span>
              </article>
            </div>

            <p className="network-footnote">
              Counterparty rosters and mandate documentation are disclosed only after a
              principal-level introduction and executed non-circumvention &amp; non-disclosure
              agreement (NCNDA, ICC 619 protocol).
            </p>
          </div>
        </section>

        {/* 10 — Contact */}
        <section id="contact" className="section section--contact">
          <div className="content content--form">
            <div className="eyebrow">Engage FIVE QUANTUM</div>
            <h2 className="headline">
              By <em>invitation</em> or qualified referral.
            </h2>
            <p className="subcopy">
              Submit your initial directive below. All enquiries are received under strict
              confidentiality and reviewed by a principal personally.
            </p>
            <EnquiryForm />
            <div className="footer-tagline">
              Sovereign-Level Discretion · Decisive Execution
            </div>
            <div className="footer-copy">
              © {new Date().getFullYear()} FIVE QUANTUM LLC · Schwarz, Ghali &amp; Dinnage Principals
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
